import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { PR_COLORS, SECTION_PAGE_SIZE } from "./constants";

/**
 * Flat inside the Paper that already draws a border — a second border around the
 * grid would read as a box inside a box.
 */
const GRID_SX = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: PR_COLORS.tableHead,
    borderBottom: `1px solid ${PR_COLORS.border}`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700,
    fontSize: "0.8rem",
    color: PR_COLORS.heading,
  },
  "& .MuiDataGrid-cell": {
    fontSize: "0.88rem",
    borderBottom: `1px solid ${PR_COLORS.border}`,
  },
  // Nothing here is selectable, so the focus ring on a cell only reads as a
  // stray box the user cannot dismiss.
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
    {
      outline: "none",
    },
  "& .MuiDataGrid-footerContainer": {
    minHeight: 44,
    borderTop: `1px solid ${PR_COLORS.border}`,
  },
  "& .MuiTablePagination-root": {
    fontSize: "0.8rem",
    color: PR_COLORS.muted,
  },
};

/**
 * The shell every Patient Overview list section shares: titled Paper, its own
 * error and loading treatment, and a paged read-only grid.
 *
 * Loading and empty keep their own rendering rather than DataGrid's overlays —
 * an `autoHeight` grid holding no rows collapses to a sliver, which is not
 * something either state should look like.
 */
export default function SectionDataGrid({
  icon,
  title,
  subtitle,
  rows,
  columns,
  loading,
  error,
  loadingText,
  emptyText,
}) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: SECTION_PAGE_SIZE,
  });

  // A new rows array means a new Patient (or a re-read). Holding page 3 for a
  // Patient with four rows would leave an empty grid the user cannot page out
  // of, since the footer's only lever is disabled at that point.
  useEffect(() => {
    setPaginationModel((model) =>
      model.page === 0 ? model : { ...model, page: 0 },
    );
  }, [rows]);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${PR_COLORS.border}`,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          color: PR_COLORS.heading,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 0.5,
        }}
      >
        {icon} {title}
      </Typography>
      <Typography sx={{ color: PR_COLORS.muted, fontSize: "0.78rem", mb: 1.5 }}>
        {subtitle}
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography sx={{ color: PR_COLORS.muted, fontSize: "0.9rem" }}>
            {loadingText}
          </Typography>
        </Box>
      )}

      {!loading && !error && rows.length === 0 && (
        <Typography sx={{ color: PR_COLORS.muted, fontSize: "0.9rem", py: 1 }}>
          {emptyText}
        </Typography>
      )}

      {!loading && rows.length > 0 && (
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          rowHeight={44}
          columnHeaderHeight={40}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[SECTION_PAGE_SIZE]}
          disableColumnMenu
          disableRowSelectionOnClick
          sx={GRID_SX}
        />
      )}
    </Paper>
  );
}
